const ALTERNATIVE_LANGUAGE = {
  BASELINE: {
    title: 'Maintain Current Position',
    description: 'Maintain existing cybersecurity controls, insurance and financial protection.'
  },
  TRANSFER_ONLY: {
    title: 'Increase Financial Protection',
    description: 'Increase financial protection while retaining the existing cybersecurity controls.'
  },
  MITIGATION_ONLY: {
    title: 'Increase Cybersecurity Investment',
    description: 'Increase investment in cybersecurity controls while retaining the existing financial protection.'
  },
  MITIGATION_TRANSFER: {
    title: 'Strengthen Cybersecurity and Financial Protection',
    description: 'Increase cybersecurity investment and financial protection together.'
  }
}

const REASON_LANGUAGE = {
  SELECTED_MAXIMUM_GOVERNED_ECONOMIC_VALUE:
    'Captures the remaining economically justified opportunity while preserving efficient capital allocation.',
  SELECTED_MAXIMUM_VALUE:
    'Captures the remaining economically justified opportunity while preserving efficient capital allocation.',
  SELECTED_BASELINE_NO_POSITIVE_ACTION_VALUE:
    'Remaining economically attractive opportunities have been exhausted.',
  BASELINE_OUTPERFORMED:
    'Remaining economically attractive opportunities would remain unfunded.',
  LOWER_GOVERNED_ECONOMIC_VALUE:
    'Additional capital would not generate proportional economic value.'
}

const reasonCode = (value) => value?.code || value?.selectionReasonCode || value?.reasonCode || value

export const translateReason = (value) => REASON_LANGUAGE[reasonCode(value)] || null

/**
 * Translates, but never augments, an authoritative alternative. Selection and
 * reason evidence must be supplied by the authoritative decision contract.
 */
export const translateExecutiveAlternative = ({ alternative, recommended }) => {
  const language = ALTERNATIVE_LANGUAGE[alternative?.bundle?.bundleType]
  if (!language) return null
  const reason = translateReason(
    alternative?.selectionReason || alternative?.rejectionReason ||
      alternative?.selectionReasonCode || alternative?.reasonCode || alternative?.reason
  )
  return {
    ...language,
    capitalRequired: alternative?.economicSummary?.capitalRequired ?? alternative?.financialImpact?.capitalRequired ?? alternative?.economics?.capitalRequired ?? alternative?.capitalRequired,
    businessConsequence: reason || 'No authoritative comparison available.',
    recommendation: recommended ? 'Recommended.' : 'Not recommended.'
  }
}

export const translateExecutiveAlternatives = (authoritativeAlternatives = []) =>
  [...authoritativeAlternatives.reduce((groups, item) => {
    const type = item?.alternative?.bundle?.bundleType
    if (!ALTERNATIVE_LANGUAGE[type]) return groups
    const existing = groups.get(type)
    // A bundle type has one executive meaning. Preserve the strongest
    // authoritative record for that meaning without manufacturing evidence.
    if (!existing || (!existing.recommended && item.recommended)) groups.set(type, item)
    return groups
  }, new Map()).values()].map(translateExecutiveAlternative)

export const translateRationale = (rationale) => {
  const reasons = Array.isArray(rationale?.selection_reason) ? rationale.selection_reason : []
  return reasons.map(translateReason).filter(Boolean).filter((value, index, all) => all.indexOf(value) === index)
}

export const managementDecisionJourney = Object.freeze([
  'Current Portfolio',
  'Existing Protection Recognised',
  'Remaining Opportunity Identified',
  'Executive Alternatives Evaluated',
  'Economically Justified Capital',
  'Recommendation Submitted to the Board'
])
